import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { formatTimestamp } from "../lib/format";
import { todayBucharestIso, utcIntervalLabel } from "../lib/intervals";
import { cn, formatMw, formatNumber, formatPrice } from "../lib/utils";
import { useDisplay } from "../stores/display";
import { useMobileUiV1State } from "../hooks/use-media-query";
import type {
  BalancingInterval,
  BalancingPriceCurrency as PriceCurrency,
  BalancingPriceSide as PriceSide,
  BalancingResponse,
  BalancingSourceStatus,
  BalancingSourceStatusValue as SourceStatus,
  BalancingView,
  MonthlyBalancingDay as MonthlyDay,
  MonthlyBalancingInterval as MonthlyInterval,
  MonthlyBalancingResponse,
  MonthlyDayPriceSummary,
  MonthlyGranularity as MatrixGranularity,
  MonthlyPriceSource as MatrixPriceSource,
  MonthlyPriceSummaries,
  PriceRange as PriceHeatRange,
} from "../features/trading/balancing/contracts";
import {
  displayDailyBalancingPrice as displayPrice,
  selectMonthlyBalancingPrice as intervalPriceValue,
  selectMonthlyPriceSummaries as monthlyPriceSummaries,
} from "../features/trading/balancing/selectors";

export const Route = createFileRoute("/balancing")({
  component: BalancingPage,
});

const BALANCING_GROUPS = [
  { label: "Time", span: 2, className: "text-muted" },
  { label: "Imbalance prices", span: 4, className: "text-accent" },
  { label: "System direction", span: 2, className: "text-accent sm:text-sky-500" },
  { label: "Activated energy", span: 6, className: "text-warnOrange" },
  { label: "Marginal prices", span: 4, className: "text-posGreen" },
  { label: "Freshness", span: 1, className: "text-muted" },
] as const;

const HEAT_KEYS = [
  "estimatedSystemImbalance",
  "activatedReserve",
  "contractedBmVolumeUp",
  "contractedBmVolumeDown",
  "afrrUp",
  "afrrDown",
  "mfrrUp",
  "mfrrDown",
  "rrUp",
  "rrDown",
  "marginalAfrrUp",
  "marginalAfrrDown",
  "marginalMfrrUp",
  "marginalMfrrDown",
] as const;

type HeatKey = (typeof HEAT_KEYS)[number];
type HeatScale = Record<HeatKey, number>;
type PriceHeatScale = Record<PriceSide, PriceHeatRange>;
type MatrixKind = "price" | "imbalance";
type DragPointer = { clientX: number; clientY: number };

const MONTHLY_SELECTION_EDGE_PX = 48;
const MONTHLY_SELECTION_MAX_SCROLL_PX = 24;


interface MatrixRow {
  slotKey: string;
  label: string;
  cells: Map<string, MatrixCell>;
}

interface MatrixCell {
  key: string;
  slotKey: string;
  slotLabel: string;
  deliveryDate: string;
  intervals: MonthlyInterval[];
}

interface MatrixCoord {
  rowIndex: number;
  dayIndex: number;
}

interface MatrixRange {
  start: MatrixCoord;
  end: MatrixCoord;
}

interface SelectionStats {
  cellCount: number;
  intervalCount: number;
  price: NumericStats;
  imbalance: NumericStats;
  absImbalanceAvg: number | null;
  surplusIntervals: number;
  deficitIntervals: number;
  da: NumericStats;
  intraday: NumericStats;
  spreadDa: NumericStats;
  spreadIntraday: NumericStats;
}

type MonthlyPriceSummaryMetric = "bm" | "dam";

interface NumericStats {
  count: number;
  avg: number | null;
  min: number | null;
  max: number | null;
}

const DISPLAY_TIME_ZONES: Record<"CET" | "EET", string> = {
  CET: "Europe/Berlin",
  EET: "Europe/Bucharest",
};

const MONTH_OPTIONS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
] as const;

function BalancingPage() {
  const { isMobileViewport } = useMobileUiV1State();
  const zone = useDisplay((s) => s.zone);
  const setZone = useDisplay((s) => s.setZone);
  const today = todayBucharestIso();
  const [view, setView] = useState<BalancingView>("daily");
  const [date, setDate] = useState(todayBucharestIso());
  const [month, setMonth] = useState(today.slice(0, 7));
  const [priceCurrency, setPriceCurrency] = useState<PriceCurrency>("RON");
  const [granularity, setGranularity] = useState<MatrixGranularity>("hour");
  const [matrixPriceSource, setMatrixPriceSource] = useState<MatrixPriceSource>("applied");
  const [matrixPriceSide, setMatrixPriceSide] = useState<PriceSide>("positive");
  const [refreshError, setRefreshError] = useState<Error | null>(null);
  const activeView: BalancingView = isMobileViewport ? "daily" : view;

  const query = useQuery({
    queryKey: ["balancing", date],
    queryFn: () =>
      api<BalancingResponse>(
        `/balancing?deliveryDate=${encodeURIComponent(date)}`
      ),
    enabled: activeView === "daily",
  });

  const monthlyQuery = useQuery({
    queryKey: ["balancing", "monthly", month],
    queryFn: () =>
      api<MonthlyBalancingResponse>(
        `/balancing/monthly?month=${encodeURIComponent(month)}`
      ),
    enabled: activeView === "monthly",
  });

  return (
    <div className="space-y-4 px-3 py-4 sm:p-4">
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="hidden sm:block">
          <Field label="View">
            <SegmentedControl
              value={view}
              onChange={(next) => setView(next)}
              options={[
                { value: "daily", label: "Daily" },
                { value: "monthly", label: "Monthly" },
              ]}
            />
          </Field>
        </div>
        {activeView === "daily" ? (
          <Field label="Delivery date">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="min-h-11 rounded border border-border bg-transparent px-3 py-2 text-base sm:min-h-0 sm:px-2 sm:py-1 sm:text-sm"
            />
          </Field>
        ) : (
          <Field label="Month">
            <MonthDropdown value={month} onChange={setMonth} />
          </Field>
        )}
        <Field label="Zone">
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value as "CET" | "EET")}
            className="min-h-11 rounded border border-border bg-transparent px-3 py-2 text-base sm:min-h-0 sm:px-2 sm:py-1 sm:text-sm"
          >
            <option value="EET">EET</option>
            <option value="CET">CET</option>
          </select>
        </Field>
        {activeView === "monthly" && (
          <>
            <Field label="Granularity">
              <SegmentedControl
                value={granularity}
                onChange={(next) => setGranularity(next)}
                options={[
                  { value: "hour", label: "Hourly avg" },
                  { value: "qh", label: "QH" },
                ]}
              />
            </Field>
            <Field label="Price source">
              <SegmentedControl
                value={matrixPriceSource}
                onChange={(next) => setMatrixPriceSource(next)}
                options={[
                  { value: "applied", label: "Applied" },
                  { value: "final", label: "Final" },
                  { value: "estimated", label: "Estimated" },
                ]}
              />
            </Field>
            <Field label="Side">
              <SegmentedControl
                value={matrixPriceSide}
                onChange={(next) => setMatrixPriceSide(next)}
                options={[
                  { value: "positive", label: "Positive" },
                  { value: "negative", label: "Negative" },
                ]}
              />
            </Field>
          </>
        )}
        <Field label="Price currency">
          <div className="inline-flex min-h-11 overflow-hidden rounded border border-border text-sm sm:min-h-0">
            {(["RON", "EUR"] as const).map((currency) => (
              <button
                key={currency}
                type="button"
                onClick={() => setPriceCurrency(currency)}
                className={cn(
                  "min-h-11 flex-1 px-3 py-2 sm:min-h-0 sm:flex-none sm:py-1",
                  priceCurrency === currency
                    ? "bg-accent text-bg"
                    : "bg-transparent text-muted hover:text-fg hover:bg-border/20"
                )}
                aria-pressed={priceCurrency === currency}
              >
                {currency}
              </button>
            ))}
          </div>
        </Field>
        {activeView === "daily" && !isMobileViewport && (
          <DesktopBalancingRefresh date={date} onError={setRefreshError} />
        )}
        <div className="text-xs text-muted sm:ml-auto">
          FX:{" "}
          {activeView === "daily" && query.data
            ? `${query.data.fx.rate.toFixed(4)} (${query.data.fx.source})`
            : activeView === "monthly" && monthlyQuery.data
            ? monthlyFxLabel(monthlyQuery.data.days)
            : "—"}
        </div>
      </div>

      {activeView === "daily" && !isMobileViewport && refreshError && (
        <div className="text-negRed text-sm">{refreshError.message}</div>
      )}
      {activeView === "daily" && query.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {activeView === "daily" && query.isError && <div className="text-negRed text-sm">{(query.error as Error).message}</div>}
      {activeView === "monthly" && monthlyQuery.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {activeView === "monthly" && monthlyQuery.isError && (
        <div className="text-negRed text-sm">{(monthlyQuery.error as Error).message}</div>
      )}

      {activeView === "daily" && query.data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
            {query.data.sources.map((s) => (
              <SourceCard key={s.source} source={s} />
            ))}
            <div className="border border-border/60 rounded-md p-3">
              <div className="text-xs text-muted">Final prices</div>
              <div className="mt-1 flex items-center gap-2">
                <StatusDot status={query.data.finalUpload.status === "present" ? "complete" : "missing"} />
                <span className="font-mono text-sm">{query.data.finalUpload.rowCount} rows</span>
              </div>
              <div className="text-[11px] text-muted mt-1">{formatTimestamp(query.data.finalUpload.latestAt)}</div>
            </div>
          </div>

          <BalancingTable
            rows={query.data.intervals}
            zone={zone}
            priceCurrency={priceCurrency}
            fxRate={query.data.fx.rate}
          />
        </>
      )}

      {activeView === "monthly" && monthlyQuery.data && (
        <MonthlyBalancingView
          data={monthlyQuery.data}
          zone={zone}
          priceCurrency={priceCurrency}
          granularity={granularity}
          priceSource={matrixPriceSource}
          priceSide={matrixPriceSide}
        />
      )}
    </div>
  );
}

function DesktopBalancingRefresh({
  date,
  onError,
}: {
  date: string;
  onError: (error: Error | null) => void;
}) {
  const queryClient = useQueryClient();
  const refresh = useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>("/balancing/refresh", {
        method: "POST",
        body: JSON.stringify({ deliveryDate: date }),
      }),
    onSuccess: () => {
      onError(null);
      void queryClient.invalidateQueries({ queryKey: ["balancing", date] });
      void queryClient.invalidateQueries({ queryKey: ["grid"] });
    },
    onError: (error) => onError(error as Error),
  });

  return (
    <button
      type="button"
      onClick={() => {
        onError(null);
        refresh.mutate();
      }}
      disabled={refresh.isPending}
      className="inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm hover:border-accent disabled:opacity-50"
    >
      <RefreshCw className={cn("h-4 w-4", refresh.isPending && "animate-spin")} />
      Refresh
    </button>
  );
}

function MonthlyBalancingView({
  data,
  zone,
  priceCurrency,
  granularity,
  priceSource,
  priceSide,
}: {
  data: MonthlyBalancingResponse;
  zone: "CET" | "EET";
  priceCurrency: PriceCurrency;
  granularity: MatrixGranularity;
  priceSource: MatrixPriceSource;
  priceSide: PriceSide;
}) {
  const [selectionRanges, setSelectionRanges] = useState<MatrixRange[]>([]);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    start: MatrixCoord;
    current: MatrixCoord;
    scrollRoot: HTMLElement | null;
  } | null>(null);
  const dragPointerRef = useRef<DragPointer | null>(null);
  const autoScrollFrameRef = useRef<number | null>(null);
  const selectionRangesRef = useRef<MatrixRange[]>([]);
  const pendingSelectionPaintRef = useRef<MatrixRange[]>([]);
  const selectionFrameRef = useRef<number | null>(null);
  const matrix = useMemo(() => buildMonthlyMatrix(data, zone, granularity), [data, zone, granularity]);
  const stats = useMemo(
    () => selectionStats(matrix, selectionRanges, priceSource, priceSide, priceCurrency),
    [matrix, selectionRanges, priceSource, priceSide, priceCurrency]
  );
  const priceScale = useMemo(
    () => cellPriceRange(matrix, (cell) => cellPriceValue(cell, priceSource, priceSide, priceCurrency)),
    [matrix, priceSource, priceSide, priceCurrency]
  );
  const priceSummaries = useMemo(
    () => monthlyPriceSummaries(data, priceSource, priceSide, priceCurrency),
    [data, priceSource, priceSide, priceCurrency]
  );
  const imbalanceScale = useMemo(
    () => maxCellAbs(matrix, (cell) => cellImbalanceValue(cell)),
    [matrix]
  );

  const paintSelection = useCallback((ranges: MatrixRange[]) => {
    paintMonthlySelection(gridRef.current, ranges);
  }, []);

  const scheduleSelectionPaint = useCallback(
    (ranges: MatrixRange[]) => {
      pendingSelectionPaintRef.current = ranges;
      if (selectionFrameRef.current !== null) return;
      selectionFrameRef.current = window.requestAnimationFrame(() => {
        selectionFrameRef.current = null;
        paintSelection(pendingSelectionPaintRef.current);
      });
    },
    [paintSelection]
  );

  const stopAutoScroll = useCallback(() => {
    dragPointerRef.current = null;
    if (autoScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  const updateDragTarget = useCallback(
    (coord: MatrixCoord) => {
      const drag = dragRef.current;
      if (!drag) return;
      drag.current = coord;
      scheduleSelectionPaint(
        appendSelectionRange(selectionRangesRef.current, normalizeRange({ start: drag.start, end: coord }))
      );
    },
    [scheduleSelectionPaint]
  );

  const startAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) return;

    const tick = () => {
      const drag = dragRef.current;
      const pointer = dragPointerRef.current;
      if (!drag || !pointer) {
        autoScrollFrameRef.current = null;
        return;
      }

      const scrollRoot = drag.scrollRoot;
      if (scrollRoot) {
        const delta = monthlyAutoScrollDelta(scrollRoot, pointer);
        if (delta.x !== 0 || delta.y !== 0) {
          scrollRoot.scrollLeft += delta.x;
          scrollRoot.scrollTop += delta.y;
          const coord = monthlyCoordFromPointer(scrollRoot, pointer);
          if (coord) updateDragTarget(coord);
        }
      }

      autoScrollFrameRef.current = window.requestAnimationFrame(tick);
    };

    autoScrollFrameRef.current = window.requestAnimationFrame(tick);
  }, [updateDragTarget]);

  const clearSelection = useCallback(() => {
    stopAutoScroll();
    dragRef.current = null;
    selectionRangesRef.current = [];
    pendingSelectionPaintRef.current = [];
    setSelectionRanges([]);
    paintSelection([]);
  }, [paintSelection, stopAutoScroll]);

  const finishDrag = useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    const range = normalizeRange({ start: drag.start, end: drag.current });
    stopAutoScroll();
    dragRef.current = null;
    const nextSelection = appendSelectionRange(selectionRangesRef.current, range);
    selectionRangesRef.current = nextSelection;
    setSelectionRanges(nextSelection);
    paintSelection(nextSelection);
  }, [paintSelection, stopAutoScroll]);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      if (drag.scrollRoot) {
        const coord = monthlyCoordFromPointer(drag.scrollRoot, dragPointerRef.current);
        if (coord) updateDragTarget(coord);
      }
      startAutoScroll();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") clearSelection();
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", finishDrag);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", finishDrag);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [clearSelection, finishDrag, startAutoScroll, updateDragTarget]);

  useEffect(() => {
    return () => {
      if (selectionFrameRef.current !== null) window.cancelAnimationFrame(selectionFrameRef.current);
      stopAutoScroll();
    };
  }, [stopAutoScroll]);

  useEffect(() => {
    stopAutoScroll();
    dragRef.current = null;
    selectionRangesRef.current = [];
    pendingSelectionPaintRef.current = [];
    setSelectionRanges([]);
    clearMonthlyHover(gridRef.current);
    paintSelection([]);
  }, [matrix, paintSelection, stopAutoScroll]);

  useEffect(() => {
    selectionRangesRef.current = selectionRanges;
    paintSelection(selectionRanges);
  }, [paintSelection, selectionRanges]);

  const beginDrag = useCallback(
    (coord: MatrixCoord) => (event: React.MouseEvent) => {
      if (event.button !== 0) return;
      event.preventDefault();
      dragRef.current = {
        start: coord,
        current: coord,
        scrollRoot: findMonthlyScrollRoot(event.currentTarget),
      };
      dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      updateDragTarget(coord);
      startAutoScroll();
    },
    [startAutoScroll, updateDragTarget]
  );

  const enterCell = useCallback(
    (coord: MatrixCoord) => {
      paintMonthlyHover(gridRef.current, coord);
      updateDragTarget(coord);
    },
    [updateDragTarget]
  );

  return (
    <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,1fr)_18rem]">
      <div ref={gridRef} className="space-y-4 min-w-0" onMouseLeave={() => clearMonthlyHover(gridRef.current)}>
        <MonthlyMatrixTable
          title="Balancing prices"
          kind="price"
          matrix={matrix}
          zone={zone}
          priceCurrency={priceCurrency}
          priceSource={priceSource}
          priceSide={priceSide}
          scale={priceScale}
          priceSummaries={priceSummaries}
          granularity={granularity}
          onCellMouseDown={beginDrag}
          onCellEnter={enterCell}
        />
        <MonthlyMatrixTable
          title="System imbalance"
          kind="imbalance"
          matrix={matrix}
          zone={zone}
          priceCurrency={priceCurrency}
          priceSource={priceSource}
          priceSide={priceSide}
          scale={imbalanceScale}
          granularity={granularity}
          onCellMouseDown={beginDrag}
          onCellEnter={enterCell}
        />
      </div>
      <SelectionPanel stats={stats} currency={priceCurrency} onClear={clearSelection} />
    </div>
  );
}

function MonthlyMatrixTable({
  title,
  kind,
  matrix,
  zone,
  priceCurrency,
  priceSource,
  priceSide,
  scale,
  priceSummaries,
  granularity,
  onCellMouseDown,
  onCellEnter,
}: {
  title: string;
  kind: MatrixKind;
  matrix: { days: MonthlyDay[]; rows: MatrixRow[] };
  zone: "CET" | "EET";
  priceCurrency: PriceCurrency;
  priceSource: MatrixPriceSource;
  priceSide: PriceSide;
  scale: number | PriceHeatRange;
  priceSummaries?: MonthlyPriceSummaries;
  granularity: MatrixGranularity;
  onCellMouseDown: (coord: MatrixCoord) => (event: React.MouseEvent) => void;
  onCellEnter: (coord: MatrixCoord) => void;
}) {
  const unit = kind === "price" ? `${priceCurrency}/MWh` : "MW";
  const scrollClassName = cn(
    "overflow-auto border border-border/60 rounded-md select-none",
    granularity === "qh" ? "max-h-[42vh]" : "max-h-none"
  );
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-[11px] text-muted font-mono">{unit}</div>
      </div>
      <div
        data-monthly-scroll
        aria-label={`${title} by delivery day; scroll horizontally for additional days`}
        tabIndex={0}
        className={scrollClassName}
      >
        <table className="min-w-full border-separate border-spacing-0 font-mono text-[11px]">
          <thead className="sticky top-0 z-20 bg-bg">
            <tr>
              <th className="sticky left-0 z-30 bg-bg px-2 py-1.5 text-left text-muted font-normal border-b border-border/60">
                Time
              </th>
              {matrix.days.map((day) => (
                <th
                  key={day.deliveryDate}
                  className="px-2 py-1.5 text-right text-muted font-normal border-b border-border/60 min-w-16"
                  title={`${day.deliveryDate} · ${day.quarters} QHs`}
                >
                  {dayHeader(day.deliveryDate)}
                </th>
              ))}
            </tr>
            {kind === "price" && priceSummaries && (
              <>
                <MonthlyPriceSummaryRow
                  label="Avg BM"
                  metric="bm"
                  matrix={matrix}
                  summaries={priceSummaries}
                  currency={priceCurrency}
                  source={priceSource}
                  side={priceSide}
                />
                <MonthlyPriceSummaryRow
                  label="Avg DAM"
                  metric="dam"
                  matrix={matrix}
                  summaries={priceSummaries}
                  currency={priceCurrency}
                  source={priceSource}
                  side={priceSide}
                />
              </>
            )}
          </thead>
          <tbody>
            {matrix.rows.map((row, rowIndex) => (
              <tr key={row.slotKey}>
                <th className="sticky left-0 z-10 bg-bg px-2 py-1 text-left text-muted font-normal border-t border-border/30 whitespace-nowrap">
                  {row.label}
                </th>
                {matrix.days.map((day, dayIndex) => {
                  const cell = row.cells.get(day.deliveryDate) ?? null;
                  const coord = { rowIndex, dayIndex };
                  const value =
                    kind === "price"
                      ? cellPriceValue(cell, priceSource, priceSide, priceCurrency)
                      : cellImbalanceValue(cell);
                  const coverage =
                    kind === "price"
                      ? cellPriceCoverage(cell, priceSource, priceSide, priceCurrency)
                      : cellValueCoverage(cell, (interval) => interval.estimatedSystemImbalance);
                  return (
                    <td
                      key={day.deliveryDate}
                      data-monthly-row={rowIndex}
                      data-monthly-day={dayIndex}
                      title={cellTooltip(cell, row.label, day.deliveryDate, zone, priceSource, priceSide, priceCurrency)}
                      onMouseDown={onCellMouseDown(coord)}
                      onMouseEnter={() => onCellEnter(coord)}
                      className={cn(
                        "px-2 py-1 text-right whitespace-nowrap border-t border-border/30 cursor-crosshair min-w-16",
                        monthlyCellHeatClass(kind, value, scale),
                        coverage.total === 0 && "text-muted bg-border/[0.02]",
                        coverage.total > 0 && coverage.count === 0 && "text-muted"
                      )}
                    >
                      {kind === "price" ? formatPrice(value) : formatMw(value)}
                      {coverage.total > 0 && coverage.count > 0 && coverage.count < coverage.total && (
                        <span className="ml-1 text-[9px] text-muted">•</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MonthlyPriceSummaryRow({
  label,
  metric,
  matrix,
  summaries,
  currency,
  source,
  side,
}: {
  label: string;
  metric: MonthlyPriceSummaryMetric;
  matrix: { days: MonthlyDay[] };
  summaries: MonthlyPriceSummaries;
  currency: PriceCurrency;
  source: MatrixPriceSource;
  side: PriceSide;
}) {
  const range = metric === "bm" ? summaries.bmRange : summaries.damRange;
  return (
    <tr>
      <th className="sticky left-0 z-30 bg-bg px-2 py-1 text-left text-muted font-normal border-b border-border/30 whitespace-nowrap">
        {label}
      </th>
      {matrix.days.map((day, dayIndex) => {
        const summary = summaries.byDate.get(day.deliveryDate) ?? null;
        const value = summary ? (metric === "bm" ? summary.bmAvg : summary.damAvg) : null;
        const count = summary ? (metric === "bm" ? summary.bmCount : summary.damCount) : 0;
        const expected = summary?.expectedIntervals ?? day.quarters;
        return (
          <td
            key={`${metric}-${day.deliveryDate}`}
            data-monthly-summary={metric}
            data-monthly-summary-day={dayIndex}
            title={monthlyPriceSummaryTooltip(day.deliveryDate, metric, summary, currency, source, side)}
            className={cn(
              "px-2 py-1 text-right whitespace-nowrap border-b border-border/30 min-w-16",
              priceHeatClass(value, range),
              value === null && "text-muted bg-border/[0.02]"
            )}
          >
            {formatPrice(value)}
            {count > 0 && count < expected && <span className="ml-1 text-[9px] text-muted">•</span>}
          </td>
        );
      })}
    </tr>
  );
}

function SelectionPanel({
  stats,
  currency,
  onClear,
}: {
  stats: SelectionStats;
  currency: PriceCurrency;
  onClear: () => void;
}) {
  return (
    <aside className="border border-border/60 rounded-md p-3 space-y-3 h-fit 2xl:sticky 2xl:top-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">Selected intervals</div>
          <div className="text-[11px] text-muted font-mono">
            {stats.cellCount} cells · {stats.intervalCount} QHs
          </div>
        </div>
        <button type="button" onClick={onClear} className="text-xs text-muted hover:text-fg">
          Clear
        </button>
      </div>
      <SelectionMetricGroup title="Balancing price" stats={stats.price} unit={`${currency}/MWh`} />
      <SelectionMetricGroup title="System imbalance" stats={stats.imbalance} unit="MW" />
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <MetricChip label="Abs avg" value={formatMw(stats.absImbalanceAvg)} unit="MW" />
        <MetricChip label="Surplus" value={String(stats.surplusIntervals)} unit="QHs" />
        <MetricChip label="Deficit" value={String(stats.deficitIntervals)} unit="QHs" />
        <MetricChip label="ID priced" value={formatNumber(stats.intraday.count, 0)} unit="QHs" />
      </div>
      <SelectionMetricGroup title="Day-ahead" stats={stats.da} unit={`${currency}/MWh`} />
      <SelectionMetricGroup title="Intraday" stats={stats.intraday} unit={`${currency}/MWh`} />
      <SelectionMetricGroup title="BM vs DA" stats={stats.spreadDa} unit={`${currency}/MWh`} />
      <SelectionMetricGroup title="BM vs ID" stats={stats.spreadIntraday} unit={`${currency}/MWh`} />
    </aside>
  );
}

function SelectionMetricGroup({ title, stats, unit }: { title: string; stats: NumericStats; unit: string }) {
  return (
    <div>
      <div className="text-xs text-muted mb-1">{title}</div>
      <div className="grid grid-cols-3 gap-2">
        <MetricChip label="Avg" value={formatPrice(stats.avg)} unit={unit} />
        <MetricChip label="Min" value={formatPrice(stats.min)} unit={unit} />
        <MetricChip label="Max" value={formatPrice(stats.max)} unit={unit} />
      </div>
    </div>
  );
}

function MetricChip({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div className="border border-border/50 rounded px-2 py-1">
      <div className="text-[10px] text-muted">{label}</div>
      <div className="font-mono text-xs truncate" title={`${value} ${unit}`}>
        {value}
      </div>
    </div>
  );
}

function SourceCard({ source }: { source: BalancingSourceStatus }) {
  const coverage = source.coverage;
  const primaryLabel = coverage
    ? `${coverage.completeRows}/${coverage.expectedRows} complete`
    : `${source.rowCount} rows`;
  const detailLabel = coverage
    ? `${coverage.rawRows} raw rows · ${coverage.populatedRows} with values${
        coverage.missingRows > 0 ? ` · ${coverage.missingRows} missing` : ""
      }`
    : null;

  return (
    <div className="border border-border/60 rounded-md p-3">
      <div className="text-xs text-muted">{labelSource(source.source)}</div>
      <div className="mt-1 flex items-center gap-2">
        <StatusDot status={source.status} />
        <span className="font-mono text-sm">{primaryLabel}</span>
      </div>
      <div className="text-[11px] text-muted mt-1">{formatTimestamp(source.finishedAt)}</div>
      {detailLabel && <div className="text-[11px] text-muted mt-1">{detailLabel}</div>}
      {source.error && <div className="text-[11px] text-negRed truncate mt-1">{source.error}</div>}
    </div>
  );
}

function BalancingTable({
  rows,
  zone,
  priceCurrency,
  fxRate,
}: {
  rows: BalancingInterval[];
  zone: "CET" | "EET";
  priceCurrency: PriceCurrency;
  fxRate: number;
}) {
  const heatScale = useMemo(() => buildHeatScale(rows), [rows]);
  const priceHeatScale = useMemo(
    () => buildPriceHeatScale(rows, priceCurrency, fxRate),
    [rows, priceCurrency, fxRate]
  );
  const priceUnit = `${priceCurrency}/MWh`;

  return (
    <div className="space-y-2">
      <BalancingLegend priceUnit={priceUnit} />
      <div
        aria-label="Quarter-hour balancing detail"
        tabIndex={0}
        className="max-h-[calc(100dvh-14rem)] overflow-auto rounded-md border border-border/60 overscroll-contain sm:max-h-[calc(100vh-17.5rem)]"
      >
        <table className="min-w-full font-mono text-[11px] border-separate border-spacing-0">
          <thead className="sticky top-0 z-10 bg-bg">
            <tr className="text-[10px] uppercase tracking-wider">
              {BALANCING_GROUPS.map((g) => (
                <th
                  key={g.label}
                  colSpan={g.span}
                  className={cn("px-2 py-1.5 font-semibold text-center border-b border-border/60", g.className)}
                >
                  {g.label}
                </th>
              ))}
            </tr>
            <tr className="text-muted">
              <Th>QH</Th>
              <Th>Interval</Th>
              <Th className="text-right bg-border/10">Est + {priceCurrency}</Th>
              <Th className="text-right bg-border/10">Est - {priceCurrency}</Th>
              <Th className="text-right bg-border/10">Final + {priceCurrency}</Th>
              <Th className="text-right bg-border/10">Final - {priceCurrency}</Th>
              <Th className="text-right bg-sky-500/5">System</Th>
              <Th className="bg-sky-500/5">Type</Th>
              <Th className="text-right bg-warnOrange/5">aFRR U</Th>
              <Th className="text-right bg-warnOrange/5">aFRR D</Th>
              <Th className="text-right bg-warnOrange/5">mFRR U</Th>
              <Th className="text-right bg-warnOrange/5">mFRR D</Th>
              <Th className="text-right bg-warnOrange/5">RR U</Th>
              <Th className="text-right bg-warnOrange/5">RR D</Th>
              <Th className="text-right bg-posGreen/5">Marg aU</Th>
              <Th className="text-right bg-posGreen/5">Marg aD</Th>
              <Th className="text-right bg-posGreen/5">Marg mU</Th>
              <Th className="text-right bg-posGreen/5">Marg mD</Th>
              <Th>Fresh</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={r.qhIndex}
                className={cn("hover:bg-border/10", idx % 2 === 1 && "bg-border/[0.03]")}
              >
                <Td className={rowDirectionClass(r)}>QH-{r.qhIndex}</Td>
                <Td>{utcIntervalLabel(r.intervalStart, r.intervalEnd, zone)}</Td>
                <PriceTd
                  value={displayPrice(r, "estimated", "positive", priceCurrency, fxRate)}
                  side="positive"
                  scale={priceHeatScale}
                />
                <PriceTd
                  value={displayPrice(r, "estimated", "negative", priceCurrency, fxRate)}
                  side="negative"
                  scale={priceHeatScale}
                />
                <PriceTd
                  value={displayPrice(r, "final", "positive", priceCurrency, fxRate)}
                  side="positive"
                  scale={priceHeatScale}
                />
                <PriceTd
                  value={displayPrice(r, "final", "negative", priceCurrency, fxRate)}
                  side="negative"
                  scale={priceHeatScale}
                />
                <HeatTd
                  value={r.estimatedSystemImbalance}
                  metric="estimatedSystemImbalance"
                  scale={heatScale}
                  direction={systemDirection(r.estimatedSystemImbalance)}
                  formatter={formatMw}
                />
                <Td className={cn("font-semibold", imbalanceTypeClass(r))}>{r.imbalanceType ?? "—"}</Td>
                <HeatTd value={r.afrrUp} metric="afrrUp" scale={heatScale} direction="up" formatter={formatMw} />
                <HeatTd value={r.afrrDown} metric="afrrDown" scale={heatScale} direction="down" formatter={formatMw} />
                <HeatTd value={r.mfrrUp} metric="mfrrUp" scale={heatScale} direction="up" formatter={formatMw} />
                <HeatTd value={r.mfrrDown} metric="mfrrDown" scale={heatScale} direction="down" formatter={formatMw} />
                <HeatTd value={r.rrUp} metric="rrUp" scale={heatScale} direction="up" formatter={formatMw} />
                <HeatTd value={r.rrDown} metric="rrDown" scale={heatScale} direction="down" formatter={formatMw} />
                <HeatTd value={r.marginalAfrrUp} metric="marginalAfrrUp" scale={heatScale} direction="up" formatter={formatPrice} />
                <HeatTd value={r.marginalAfrrDown} metric="marginalAfrrDown" scale={heatScale} direction="down" formatter={formatPrice} />
                <HeatTd value={r.marginalMfrrUp} metric="marginalMfrrUp" scale={heatScale} direction="up" formatter={formatPrice} />
                <HeatTd value={r.marginalMfrrDown} metric="marginalMfrrDown" scale={heatScale} direction="down" formatter={formatPrice} />
                <Td>
                  <FreshnessBadge row={r} />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BalancingLegend({ priceUnit }: { priceUnit: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted">
      <LegendChip className="border-posGreen/40 bg-posGreen/10 text-posGreen" label="final" />
      <LegendChip className="border-warnOrange/50 bg-warnOrange/10 text-warnOrange" label="estimated" />
      <LegendChip className="border-accent/40 bg-accent/10 text-accent sm:border-sky-500/40 sm:bg-sky-500/10 sm:text-sky-500" label="surplus" />
      <LegendChip className="border-warnOrange/50 bg-warnOrange/10 text-warnOrange sm:border-amber-500/50 sm:bg-amber-500/10 sm:text-amber-500" label="deficit" />
      <span>Imbalance prices in {priceUnit}.</span>
      <span>Stronger cell tint means larger same-day magnitude.</span>
    </div>
  );
}

function LegendChip({ className, label }: { className: string; label: string }) {
  return <span className={cn("rounded border px-1.5 py-0.5 font-mono", className)}>{label}</span>;
}

function PriceTd({ value, side, scale }: { value: number | null; side: PriceSide; scale: PriceHeatScale }) {
  return (
    <Td
      className={cn(
        "text-right",
        priceHeatClass(value, scale[side]),
        value === null && "text-muted"
      )}
    >
      {formatPrice(value)}
    </Td>
  );
}

function HeatTd({
  value,
  metric,
  scale,
  direction,
  formatter,
}: {
  value: number | null;
  metric: HeatKey;
  scale: HeatScale;
  direction: "up" | "down" | "surplus" | "deficit" | "neutral";
  formatter: (n: number | null | undefined) => string;
}) {
  return (
    <Td className={cn("text-right", heatClass(value, scale[metric], direction), value === null && "text-muted")}>
      {formatter(value)}
    </Td>
  );
}

function FreshnessBadge({ row }: { row: BalancingInterval }) {
  const source =
    row.finalIngestedAt !== null
      ? "final"
      : row.estimatedFetchedAt !== null
      ? "estimated"
      : row.updatedAt !== null
      ? "partial"
      : "missing";
  const cls =
    source === "final"
      ? "border-posGreen/40 bg-posGreen/10 text-posGreen"
      : source === "estimated"
      ? "border-warnOrange/50 bg-warnOrange/10 text-warnOrange"
      : source === "partial"
      ? "border-accent/40 bg-accent/10 text-accent"
      : "border-negRed/40 bg-negRed/10 text-negRed";
  const timestamp = formatTimestamp(
    row.finalIngestedAt ?? row.estimatedFetchedAt ?? row.updatedAt
  );
  return (
    <span
      className={cn("inline-flex min-w-16 flex-col items-center justify-center rounded border px-1.5 py-0.5 text-[10px] sm:flex-row", cls)}
      title={timestamp}
    >
      {source}
      <span className="whitespace-nowrap text-[9px] font-normal sm:hidden">{timestamp}</span>
    </span>
  );
}

function buildHeatScale(rows: BalancingInterval[]): HeatScale {
  return HEAT_KEYS.reduce((acc, key) => {
    acc[key] = Math.max(...rows.map((r) => Math.abs(r[key] ?? 0)), 0);
    return acc;
  }, {} as HeatScale);
}

function buildPriceHeatScale(
  rows: BalancingInterval[],
  currency: PriceCurrency,
  fxRate: number
): PriceHeatScale {
  return {
    positive: displayedPriceRange(rows, currency, fxRate, "positive"),
    negative: displayedPriceRange(rows, currency, fxRate, "negative"),
  };
}

function displayedPriceRange(
  rows: BalancingInterval[],
  currency: PriceCurrency,
  fxRate: number,
  side: PriceSide
): PriceHeatRange {
  return numericRange(
    rows.flatMap((r) => [
      displayPrice(r, "estimated", side, currency, fxRate),
      displayPrice(r, "final", side, currency, fxRate),
    ])
  );
}

function heatClass(
  value: number | null,
  maxAbs: number,
  direction: "up" | "down" | "surplus" | "deficit" | "neutral"
): string {
  if (value === null || maxAbs <= 0) return "";
  const ratio = Math.min(Math.abs(value) / maxAbs, 1);
  const bucket = ratio >= 0.75 ? "strong" : ratio >= 0.4 ? "medium" : ratio > 0 ? "soft" : "none";
  if (bucket === "none") return "";

  if (direction === "surplus") {
    return bucket === "strong"
      ? "bg-sky-500/20 text-sky-600 dark:text-sky-300"
      : bucket === "medium"
      ? "bg-sky-500/10 text-sky-600 dark:text-sky-300"
      : "bg-sky-500/5";
  }
  if (direction === "deficit") {
    return bucket === "strong"
      ? "bg-amber-500/20 text-amber-600 dark:text-amber-300"
      : bucket === "medium"
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-300"
      : "bg-amber-500/5";
  }
  if (direction === "up") {
    return bucket === "strong"
      ? "bg-rose-500/20 text-rose-600 dark:text-rose-300"
      : bucket === "medium"
      ? "bg-rose-500/10 text-rose-600 dark:text-rose-300"
      : "bg-rose-500/5";
  }
  if (direction === "down") {
    return bucket === "strong"
      ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-300"
      : bucket === "medium"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
      : "bg-emerald-500/5";
  }
  return bucket === "strong" ? "bg-border/30" : bucket === "medium" ? "bg-border/20" : "bg-border/10";
}

function systemDirection(value: number | null): "surplus" | "deficit" | "neutral" {
  if (value === null || Math.abs(value) < 1e-9) return "neutral";
  return value > 0 ? "surplus" : "deficit";
}

function imbalanceTypeClass(row: BalancingInterval): string {
  const normalized = row.imbalanceType?.toLowerCase() ?? "";
  if (normalized.includes("surplus")) return "text-sky-500";
  if (normalized.includes("deficit")) return "text-amber-500";
  const direction = systemDirection(row.estimatedSystemImbalance);
  if (direction === "surplus") return "text-sky-500";
  if (direction === "deficit") return "text-amber-500";
  return "text-muted";
}

function rowDirectionClass(row: BalancingInterval): string {
  const direction = systemDirection(row.estimatedSystemImbalance);
  if (direction === "surplus") return "border-l-2 border-sky-500/60";
  if (direction === "deficit") return "border-l-2 border-amber-500/70";
  return "";
}

function buildMonthlyMatrix(
  data: MonthlyBalancingResponse,
  zone: "CET" | "EET",
  granularity: MatrixGranularity
): { days: MonthlyDay[]; rows: MatrixRow[] } {
  const byDay = new Map<string, MonthlyInterval[]>();
  for (const interval of data.intervals) {
    const rows = byDay.get(interval.deliveryDate) ?? [];
    rows.push(interval);
    byDay.set(interval.deliveryDate, rows);
  }

  const rowMap = new Map<string, MatrixRow>();
  for (const day of data.days) {
    const intervals = (byDay.get(day.deliveryDate) ?? []).sort((a, b) => a.qhIndex - b.qhIndex);
    const slots = daySlots(intervals, zone, granularity);
    for (const slot of slots.values()) {
      const row = rowMap.get(slot.slotKey) ?? {
        slotKey: slot.slotKey,
        label: slot.label,
        cells: new Map<string, MatrixCell>(),
      };
      row.cells.set(day.deliveryDate, {
        key: cellKey(slot.slotKey, day.deliveryDate),
        slotKey: slot.slotKey,
        slotLabel: slot.label,
        deliveryDate: day.deliveryDate,
        intervals: slot.intervals,
      });
      rowMap.set(slot.slotKey, row);
    }
  }

  const duplicateLabels = new Set<string>();
  const baseCounts = new Map<string, number>();
  for (const key of rowMap.keys()) {
    const { baseLabel } = parseSlotKey(key);
    baseCounts.set(baseLabel, (baseCounts.get(baseLabel) ?? 0) + 1);
  }
  for (const [label, count] of baseCounts) if (count > 1) duplicateLabels.add(label);

  const rows = [...rowMap.values()].sort((a, b) => slotSortValue(a.slotKey) - slotSortValue(b.slotKey));
  for (const row of rows) {
    const { baseLabel, occurrence } = parseSlotKey(row.slotKey);
    row.label = duplicateLabels.has(baseLabel) ? `${baseLabel} ${occurrenceLabel(occurrence)}` : baseLabel;
    for (const cell of row.cells.values()) cell.slotLabel = row.label;
  }
  return { days: data.days, rows };
}

function daySlots(
  intervals: MonthlyInterval[],
  zone: "CET" | "EET",
  granularity: MatrixGranularity
): Map<string, { slotKey: string; label: string; intervals: MonthlyInterval[] }> {
  const slots = new Map<string, { slotKey: string; label: string; intervals: MonthlyInterval[] }>();
  const seenQhLabels = new Map<string, number>();
  for (const interval of intervals) {
    const qhLabel = timeLabelInZone(interval.intervalStart, zone);
    const occurrence = seenQhLabels.get(qhLabel) ?? 0;
    seenQhLabels.set(qhLabel, occurrence + 1);
    const baseLabel = granularity === "qh" ? qhLabel : `${qhLabel.slice(0, 2)}:00`;
    const slotKey = `${baseLabel}|${occurrence}`;
    const slot = slots.get(slotKey) ?? { slotKey, label: baseLabel, intervals: [] };
    slot.intervals.push(interval);
    slots.set(slotKey, slot);
  }
  return slots;
}

function timeLabelInZone(iso: string, zone: "CET" | "EET"): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DISPLAY_TIME_ZONES[zone],
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const hourPart = parts.find((part) => part.type === "hour")?.value ?? "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  const hour = hourPart === "24" ? "00" : hourPart;
  return `${hour}:${minute}`;
}

function parseSlotKey(slotKey: string): { baseLabel: string; occurrence: number } {
  const [baseLabel, occurrenceRaw] = slotKey.split("|");
  return { baseLabel: baseLabel ?? "00:00", occurrence: Number(occurrenceRaw ?? 0) || 0 };
}

function slotSortValue(slotKey: string): number {
  const { baseLabel, occurrence } = parseSlotKey(slotKey);
  const [hour, minute] = baseLabel.split(":").map(Number);
  return ((hour || 0) * 60 + (minute || 0)) * 10 + occurrence;
}

function occurrenceLabel(occurrence: number): string {
  return String.fromCharCode("A".charCodeAt(0) + occurrence);
}

function cellKey(slotKey: string, deliveryDate: string): string {
  return `${slotKey}:${deliveryDate}`;
}

function cellPriceValue(
  cell: MatrixCell | null,
  source: MatrixPriceSource,
  side: PriceSide,
  currency: PriceCurrency
): number | null {
  if (!cell) return null;
  return average(cell.intervals.map((interval) => intervalPriceValue(interval, source, side, currency)));
}

function cellImbalanceValue(cell: MatrixCell | null): number | null {
  if (!cell) return null;
  return average(cell.intervals.map((interval) => interval.estimatedSystemImbalance));
}

function cellDaPrice(cell: MatrixCell | null, currency: PriceCurrency): number | null {
  if (!cell) return null;
  return average(cell.intervals.map((interval) => (currency === "EUR" ? interval.daPriceEur : interval.daPriceRon)));
}

function cellIntradayPrice(cell: MatrixCell | null, currency: PriceCurrency): number | null {
  if (!cell) return null;
  let value = 0;
  let weight = 0;
  for (const interval of cell.intervals) {
    const price = currency === "EUR" ? interval.intradayPriceEur : interval.intradayPriceRon;
    const mwh = interval.intradayVolumeMwh;
    if (price === null || !Number.isFinite(price) || mwh <= 0) continue;
    value += price * mwh;
    weight += mwh;
  }
  return weight > 0 ? value / weight : null;
}

function cellValueCoverage<T>(
  cell: MatrixCell | null,
  getValue: (interval: MonthlyInterval) => T | null | undefined
): { count: number; total: number } {
  if (!cell) return { count: 0, total: 0 };
  return {
    count: cell.intervals.filter((interval) => getValue(interval) !== null && getValue(interval) !== undefined).length,
    total: cell.intervals.length,
  };
}

function cellPriceCoverage(
  cell: MatrixCell | null,
  source: MatrixPriceSource,
  side: PriceSide,
  currency: PriceCurrency
): { count: number; total: number } {
  return cellValueCoverage(cell, (interval) => intervalPriceValue(interval, source, side, currency));
}

function average(values: Array<number | null | undefined>): number | null {
  const clean = values.filter((value): value is number => value !== null && value !== undefined && Number.isFinite(value));
  if (clean.length === 0) return null;
  return clean.reduce((sum, value) => sum + value, 0) / clean.length;
}

function numericRange(values: Array<number | null | undefined>): PriceHeatRange {
  const clean = values.filter((value): value is number => value !== null && value !== undefined && Number.isFinite(value));
  if (clean.length === 0) return { min: 0, max: 0 };
  return { min: Math.min(...clean), max: Math.max(...clean) };
}

function priceHeatClass(value: number | null, range: PriceHeatRange): string {
  if (value === null || range.max <= range.min) return "";
  const ratio = clamp((value - range.min) / (range.max - range.min), 0, 1);
  if (ratio >= 0.85) return "bg-amber-500/20 text-amber-600 dark:text-amber-300";
  if (ratio >= 0.65) return "bg-amber-500/10 text-amber-600 dark:text-amber-300";
  if (ratio <= 0.15) return "bg-sky-500/20 text-sky-600 dark:text-sky-300";
  if (ratio <= 0.35) return "bg-sky-500/10 text-sky-600 dark:text-sky-300";
  return "bg-border/10";
}

function cellPriceRange(
  matrix: { rows: MatrixRow[] },
  getValue: (cell: MatrixCell) => number | null
): PriceHeatRange {
  const values: Array<number | null> = [];
  for (const row of matrix.rows) {
    for (const cell of row.cells.values()) {
      values.push(getValue(cell));
    }
  }
  return numericRange(values);
}

function maxCellAbs(
  matrix: { rows: MatrixRow[] },
  getValue: (cell: MatrixCell) => number | null
): number {
  let max = 0;
  for (const row of matrix.rows) {
    for (const cell of row.cells.values()) {
      const value = getValue(cell);
      if (value !== null) max = Math.max(max, Math.abs(value));
    }
  }
  return max;
}

function monthlyCellHeatClass(kind: MatrixKind, value: number | null, scale: number | PriceHeatRange): string {
  if (kind === "price") return priceHeatClass(value, scale as PriceHeatRange);
  return heatClass(value, typeof scale === "number" ? scale : 0, systemDirection(value));
}

function cellTooltip(
  cell: MatrixCell | null,
  fallbackLabel: string,
  deliveryDate: string,
  zone: "CET" | "EET",
  source: MatrixPriceSource,
  side: PriceSide,
  currency: PriceCurrency
): string {
  const intervalLabel =
    cell && cell.intervals.length > 0
      ? utcIntervalLabel(cell.intervals[0]!.intervalStart, cell.intervals[cell.intervals.length - 1]!.intervalEnd, zone)
      : `${fallbackLabel} ${zone}`;
  const price = cellPriceValue(cell, source, side, currency);
  const imbalance = cellImbalanceValue(cell);
  const da = cellDaPrice(cell, currency);
  const intraday = cellIntradayPrice(cell, currency);
  const coverage = cellPriceCoverage(cell, source, side, currency);
  return [
    `${deliveryDate}, ${intervalLabel}`,
    `Balancing price: ${tooltipPrice(price, currency)}`,
    `System imbalance: ${tooltipMw(imbalance)}`,
    `Day-ahead price: ${tooltipPrice(da, currency)}`,
    `Intraday price: ${tooltipPrice(intraday, currency)}`,
    `Coverage: ${coverage.count}/${coverage.total} QHs`,
    `Source: ${source} ${side}`,
  ].join("\n");
}

function tooltipPrice(value: number | null, currency: PriceCurrency): string {
  return value === null ? "N/A" : `${formatPrice(value)} ${currency}/MWh`;
}

function tooltipMw(value: number | null): string {
  return value === null ? "N/A" : `${formatMw(value)} MW`;
}

function monthlyPriceSummaryTooltip(
  deliveryDate: string,
  metric: MonthlyPriceSummaryMetric,
  summary: MonthlyDayPriceSummary | null,
  currency: PriceCurrency,
  source: MatrixPriceSource,
  side: PriceSide
): string {
  const value = summary ? (metric === "bm" ? summary.bmAvg : summary.damAvg) : null;
  const count = summary ? (metric === "bm" ? summary.bmCount : summary.damCount) : 0;
  const expected = summary?.expectedIntervals ?? 0;
  const label = metric === "bm" ? `Avg balancing price (${source} ${side})` : "Avg DAM price";
  return [
    deliveryDate,
    `${label}: ${tooltipPrice(value, currency)}`,
    `Coverage: ${count}/${expected} QHs`,
    `Rows available: ${summary?.availableIntervals ?? 0}`,
    "Simple average",
  ].join("\n");
}

function paintMonthlyHover(root: HTMLElement | null, coord: MatrixCoord): void {
  if (!root) return;
  clearMonthlyHover(root);
  const row = String(coord.rowIndex);
  const day = String(coord.dayIndex);
  root
    .querySelectorAll<HTMLElement>(`[data-monthly-row="${row}"], [data-monthly-day="${day}"]`)
    .forEach((el) => el.classList.add("monthly-cell-related"));
  root
    .querySelectorAll<HTMLElement>(`[data-monthly-row="${row}"][data-monthly-day="${day}"]`)
    .forEach((el) => {
      el.classList.remove("monthly-cell-related");
      el.classList.add("monthly-cell-active");
    });
}

function clearMonthlyHover(root: HTMLElement | null): void {
  if (!root) return;
  root
    .querySelectorAll<HTMLElement>(".monthly-cell-active, .monthly-cell-related")
    .forEach((el) => el.classList.remove("monthly-cell-active", "monthly-cell-related"));
}

function findMonthlyScrollRoot(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  return target.closest<HTMLElement>("[data-monthly-scroll]");
}

function monthlyAutoScrollDelta(root: HTMLElement, pointer: DragPointer): { x: number; y: number } {
  const rect = root.getBoundingClientRect();
  return {
    x: edgeScrollDelta(pointer.clientX, rect.left, rect.right),
    y: edgeScrollDelta(pointer.clientY, rect.top, rect.bottom),
  };
}

function edgeScrollDelta(pointerPosition: number, min: number, max: number): number {
  const beforeEdge = pointerPosition - min;
  if (beforeEdge < MONTHLY_SELECTION_EDGE_PX) return -edgeScrollSpeed(MONTHLY_SELECTION_EDGE_PX - beforeEdge);
  const afterEdge = max - pointerPosition;
  if (afterEdge < MONTHLY_SELECTION_EDGE_PX) return edgeScrollSpeed(MONTHLY_SELECTION_EDGE_PX - afterEdge);
  return 0;
}

function edgeScrollSpeed(edgeDepth: number): number {
  const strength = clamp(edgeDepth / MONTHLY_SELECTION_EDGE_PX, 0, 1);
  return Math.max(2, Math.ceil(strength * MONTHLY_SELECTION_MAX_SCROLL_PX));
}

function monthlyCoordFromPointer(root: HTMLElement, pointer: DragPointer): MatrixCoord | null {
  const rect = root.getBoundingClientRect();
  const x = clamp(pointer.clientX, rect.left + 1, rect.right - 1);
  const y = clamp(pointer.clientY, rect.top + 1, rect.bottom - 1);
  const hit = document.elementFromPoint(x, y);
  const hitCell = hit?.closest<HTMLElement>("[data-monthly-row][data-monthly-day]");
  if (hitCell && root.contains(hitCell)) return monthlyCoordFromCell(hitCell);

  let bestCell: HTMLElement | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  root.querySelectorAll<HTMLElement>("[data-monthly-row][data-monthly-day]").forEach((cell) => {
    const cellRect = cell.getBoundingClientRect();
    if (
      cellRect.bottom < rect.top ||
      cellRect.top > rect.bottom ||
      cellRect.right < rect.left ||
      cellRect.left > rect.right
    ) {
      return;
    }
    const dx = x < cellRect.left ? cellRect.left - x : x > cellRect.right ? x - cellRect.right : 0;
    const dy = y < cellRect.top ? cellRect.top - y : y > cellRect.bottom ? y - cellRect.bottom : 0;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      bestCell = cell;
    }
  });

  return bestCell ? monthlyCoordFromCell(bestCell) : null;
}

function monthlyCoordFromCell(cell: HTMLElement): MatrixCoord | null {
  const rowIndex = Number(cell.dataset.monthlyRow);
  const dayIndex = Number(cell.dataset.monthlyDay);
  if (!Number.isInteger(rowIndex) || !Number.isInteger(dayIndex)) return null;
  return { rowIndex, dayIndex };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function paintMonthlySelection(root: HTMLElement | null, ranges: MatrixRange[]): void {
  if (!root) return;
  root
    .querySelectorAll<HTMLElement>(".monthly-cell-selected")
    .forEach((el) => el.classList.remove("monthly-cell-selected"));
  for (const range of ranges) {
    for (let rowIndex = range.start.rowIndex; rowIndex <= range.end.rowIndex; rowIndex++) {
      for (let dayIndex = range.start.dayIndex; dayIndex <= range.end.dayIndex; dayIndex++) {
        root
          .querySelectorAll<HTMLElement>(`[data-monthly-row="${rowIndex}"][data-monthly-day="${dayIndex}"]`)
          .forEach((el) => el.classList.add("monthly-cell-selected"));
      }
    }
  }
}

function normalizeRange(range: MatrixRange): MatrixRange {
  return {
    start: {
      rowIndex: Math.min(range.start.rowIndex, range.end.rowIndex),
      dayIndex: Math.min(range.start.dayIndex, range.end.dayIndex),
    },
    end: {
      rowIndex: Math.max(range.start.rowIndex, range.end.rowIndex),
      dayIndex: Math.max(range.start.dayIndex, range.end.dayIndex),
    },
  };
}

function rangesEqual(a: MatrixRange, b: MatrixRange): boolean {
  return (
    a.start.rowIndex === b.start.rowIndex &&
    a.start.dayIndex === b.start.dayIndex &&
    a.end.rowIndex === b.end.rowIndex &&
    a.end.dayIndex === b.end.dayIndex
  );
}

function appendSelectionRange(ranges: MatrixRange[], range: MatrixRange): MatrixRange[] {
  const normalized = normalizeRange(range);
  if (ranges.some((existing) => rangesEqual(existing, normalized))) return ranges;
  return [...ranges, normalized];
}

function selectionStats(
  matrix: { days: MonthlyDay[]; rows: MatrixRow[] },
  ranges: MatrixRange[],
  source: MatrixPriceSource,
  side: PriceSide,
  currency: PriceCurrency
): SelectionStats {
  if (ranges.length === 0) return blankSelectionStats();
  const intervals: MonthlyInterval[] = [];
  let cellCount = 0;
  for (const { rowIndex, dayIndex } of selectedMatrixCoords(ranges)) {
    const row = matrix.rows[rowIndex];
    const day = matrix.days[dayIndex];
    if (!row || !day) continue;
    cellCount++;
    const cell = row.cells.get(day.deliveryDate);
    if (cell) intervals.push(...cell.intervals);
  }

  const priceValues = intervals.map((interval) => intervalPriceValue(interval, source, side, currency));
  const imbalanceValues = intervals.map((interval) => interval.estimatedSystemImbalance);
  const daValues = intervals.map((interval) => (currency === "EUR" ? interval.daPriceEur : interval.daPriceRon));
  const intradayValues = intervals.map((interval) => (currency === "EUR" ? interval.intradayPriceEur : interval.intradayPriceRon));
  const intradayWeights = intervals.map((interval) => interval.intradayVolumeMwh);
  const spreadDa = intervals.map((interval) => {
    const price = intervalPriceValue(interval, source, side, currency);
    const da = currency === "EUR" ? interval.daPriceEur : interval.daPriceRon;
    return price !== null && da !== null ? price - da : null;
  });
  const spreadIntraday = intervals.map((interval) => {
    const price = intervalPriceValue(interval, source, side, currency);
    const intraday = currency === "EUR" ? interval.intradayPriceEur : interval.intradayPriceRon;
    return price !== null && intraday !== null ? price - intraday : null;
  });
  const imbalanceClean = imbalanceValues.filter(
    (value): value is number => value !== null && value !== undefined && Number.isFinite(value)
  );

  return {
    cellCount,
    intervalCount: intervals.length,
    price: numericStats(priceValues),
    imbalance: numericStats(imbalanceValues),
    absImbalanceAvg: imbalanceClean.length
      ? imbalanceClean.reduce((sum, value) => sum + Math.abs(value), 0) / imbalanceClean.length
      : null,
    surplusIntervals: imbalanceClean.filter((value) => value > 0).length,
    deficitIntervals: imbalanceClean.filter((value) => value < 0).length,
    da: numericStats(daValues),
    intraday: numericStats(intradayValues, intradayWeights),
    spreadDa: numericStats(spreadDa),
    spreadIntraday: numericStats(spreadIntraday),
  };
}

function selectedMatrixCoords(ranges: MatrixRange[]): MatrixCoord[] {
  const seen = new Set<string>();
  const coords: MatrixCoord[] = [];
  for (const range of ranges) {
    for (let rowIndex = range.start.rowIndex; rowIndex <= range.end.rowIndex; rowIndex++) {
      for (let dayIndex = range.start.dayIndex; dayIndex <= range.end.dayIndex; dayIndex++) {
        const key = `${rowIndex}:${dayIndex}`;
        if (seen.has(key)) continue;
        seen.add(key);
        coords.push({ rowIndex, dayIndex });
      }
    }
  }
  return coords;
}

function numericStats(values: Array<number | null | undefined>, weights?: number[]): NumericStats {
  const clean: { value: number; weight: number }[] = [];
  values.forEach((value, idx) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return;
    const weight = weights ? weights[idx] ?? 0 : 1;
    if (weights && weight <= 0) return;
    clean.push({ value, weight });
  });
  if (clean.length === 0) return { count: 0, avg: null, min: null, max: null };
  const totalWeight = clean.reduce((sum, item) => sum + item.weight, 0);
  return {
    count: clean.length,
    avg: clean.reduce((sum, item) => sum + item.value * item.weight, 0) / totalWeight,
    min: Math.min(...clean.map((item) => item.value)),
    max: Math.max(...clean.map((item) => item.value)),
  };
}

function blankSelectionStats(): SelectionStats {
  const empty = { count: 0, avg: null, min: null, max: null };
  return {
    cellCount: 0,
    intervalCount: 0,
    price: empty,
    imbalance: empty,
    absImbalanceAvg: null,
    surplusIntervals: 0,
    deficitIntervals: 0,
    da: empty,
    intraday: empty,
    spreadDa: empty,
    spreadIntraday: empty,
  };
}

function dayHeader(deliveryDate: string): string {
  const [, month, day] = deliveryDate.split("-");
  return `${month}/${day}`;
}

function monthlyFxLabel(days: MonthlyDay[]): string {
  const rates = days.map((day) => day.fx.rate).filter((rate) => rate > 0);
  if (rates.length === 0) return "—";
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  if (Math.abs(min - max) < 1e-9) return min.toFixed(4);
  return `${min.toFixed(4)}-${max.toFixed(4)}`;
}

function MonthDropdown({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [yearRaw, monthRaw] = value.split("-");
  const fallbackYear = Number(todayBucharestIso().slice(0, 4));
  const selectedYear = Number(yearRaw) || fallbackYear;
  const selectedMonth = Number(monthRaw) || 1;
  const years = useMemo(() => monthYearOptions(selectedYear), [selectedYear]);

  const updateMonth = (nextMonth: number) => onChange(formatMonthValue(selectedYear, nextMonth));
  const updateYear = (nextYear: number) => onChange(formatMonthValue(nextYear, selectedMonth));

  return (
    <div className="inline-flex gap-2">
      <select
        aria-label="Month"
        value={selectedMonth}
        onChange={(e) => updateMonth(Number(e.target.value))}
        className="min-h-11 rounded border border-border bg-transparent px-3 py-2 text-base sm:min-h-0 sm:px-2 sm:py-1 sm:text-sm"
      >
        {MONTH_OPTIONS.map((month) => (
          <option key={month.value} value={month.value}>
            {month.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Year"
        value={selectedYear}
        onChange={(e) => updateYear(Number(e.target.value))}
        className="min-h-11 rounded border border-border bg-transparent px-3 py-2 text-base sm:min-h-0 sm:px-2 sm:py-1 sm:text-sm"
      >
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );
}

function monthYearOptions(selectedYear: number): number[] {
  const currentYear = Number(todayBucharestIso().slice(0, 4));
  const start = Math.min(selectedYear, currentYear - 4);
  const end = Math.max(selectedYear, currentYear + 1);
  const years = [];
  for (let year = end; year >= start; year--) years.push(year);
  return years;
}

function formatMonthValue(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="inline-flex min-h-11 max-w-full overflow-x-auto rounded border border-border text-sm sm:min-h-0">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "min-h-11 px-3 py-2 whitespace-nowrap sm:min-h-0 sm:py-1",
            value === option.value ? "bg-accent text-bg" : "bg-transparent text-muted hover:text-fg hover:bg-border/20"
          )}
          aria-pressed={value === option.value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function StatusDot({ status }: { status: SourceStatus }) {
  const cls =
    status === "complete"
      ? "bg-posGreen"
      : status === "partial"
      ? "bg-warnOrange"
      : status === "error"
      ? "bg-negRed"
      : "bg-border";
  return <span className={cn("h-2.5 w-2.5 rounded-full", cls)} title={status} />;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-muted">
      {label}
      {children}
    </label>
  );
}

function labelSource(source: BalancingSourceStatus["source"]): string {
  if (source === "estimatedImbalancePrices") return "Estimated prices";
  if (source === "estimatedPowerSystemImbalance") return "System imbalance";
  if (source === "activatedBalancingEnergyOverview") return "Activated energy";
  if (source === "marginalPricesOverview") return "Marginal prices";
  return source;
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-2 py-1.5 font-normal whitespace-nowrap text-left", className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-2 py-1 whitespace-nowrap border-t border-border/30", className)}>{children}</td>;
}
